<app-page-header
  eyebrow="Как смятаме"
  title="Методология"
  lead="Всяка цифра в Имотомер се изчислява по формулите по-долу. Кодът на метриките е отворен: src/app/core/stats/metrics.ts."
/>

<div class="prose">
  <section class="card">
    <h2>Откъде идват данните</h2>
    <p>
      Три слоя, всеки от посочен източник. Нищо на тази страница не е моделирано или
      симулирано; където един източник не стига, това е казано изрично.
    </p>
    <dl>
      <dt>Обяви — imot.bg</dt>
      <dd>
        Живите обяви на портала, обходени през публичния му <code>sitemap</code>. Взимат се само
        <b>жилища</b> във всички 75 града в страната с население над 10 000 жители (27-те областни
        центъра и 48-те големи необластни и курортни града): парцели, земеделска земя, гаражи, офиси, магазини,
        складове и бизнес имоти отпадат, защото цена на кв.м на парцел и на апартамент не
        принадлежат на едно разпределение. Отпадат и селата, и имотите в чужбина.
        @if (dataNote(); as note) {
          <br /><b>{{ note }}</b>
        }
      </dd>
      <dt>Индекс на цените и наеми — Eurostat</dt>
      <dd>
        Тримесечният индекс на жилищните цени (<code>prc_hpi_q</code>) и месечният индекс на
        наемите (HICP <code>CP041</code>), от 2015 г. насам. <b>Индексът на цените е
        национален</b> — Eurostat не го публикува по региони, така че всички региони носят
        едно и също число. Това е ограничение на източника, не оценка на наш модел.
      </dd>
      <dt>Доходи — Eurostat, по региони</dt>
      <dd>
        Нетен разполагаем доход на човек по статистически район (<code>nama_10r_2hhinc</code>).
        Годишен — няма публикуван вътрешногодишен ход, затова стойността се държи постоянна през
        четирите тримесечия на своята година, вместо да се интерполира движение, което никой
        не е наблюдавал.
      </dd>
      <dt>Лихви по ипотеки — ЕЦБ</dt>
      <dd>
        Лихви по нови жилищни кредити за домакинства в България, месечно от 2015 г.
        (ЕЦБ, MIR). Това е редът, който БНБ отчита, поднесен машинно четимо.
      </dd>
    </dl>
    <p>
      Всички суми са в евро; левовата равностойност е по фиксирания курс 1 € = 1,95583 лв.
      Медианата е предпочитана пред средната стойност, защото е устойчива на екстремни обяви.
    </p>
  </section>

  <section class="card">
    <h2>Ниво 1 — цени и наличност</h2>
    <dl>
      <dt>Медиана офертна цена €/м²</dt>
      <dd>
        Медиана на офертната цена на кв.м от всички активни обяви за продажба в периода (месец или година). Смята се и по град, квартал, тип имот, строителство и година на строеж.
        <br />
        <b>Важно пояснение:</b> Всички цени в платформата са <b>пазарни офертни цени</b> (asking prices), обявени от продавачите и агенциите в порталите (включително техните последващи актуализации и намаления). Те <b>не са</b> крайните цени от нотариално изповядани сделки в Агенция по вписванията, тъй като вписаните сделки в България не съдържат публично достъпни данни за конкретните обяви и техните характеристики.
      </dd>
      <dt>Тенденция и промени</dt>
      <dd>МоМ = промяна спрямо предходния месец; ГоГ = промяна спрямо същия месец предишната година. Годишният изглед агрегира месечните медиани чрез медиана.</dd>
      <dt>Активни обяви</dt>
      <dd>Брой обяви за продажба със снимка в съответния месец.</dd>
      <dt>Прогноза на цената (пунктирана линия)</dt>
      <dd>
        Линейна екстраполация (OLS) на медианата €/м² за последните 12 месеца, продължена 6
        месеца напред. Това е най-простото възможно продължение на скорошната тенденция при
        допускане, че тя остава линейна — <b>не е прогнозен модел</b>: не отчита сезонност,
        шокове, лихвена политика или обрат на тренда. Показва се само когато трендът е
        достатъчно устойчив (R² на регресията ≥ 0,25); при нестабилни серии линията се скрива,
        вместо да подвежда.
      </dd>
    </dl>
  </section>

  <section class="card">
    <h2>Ниво 2 — здраве на пазара</h2>
    <dl>
      <dt>Брутна доходност от наем</dt>
      <dd>(медианен наем €/м²/мес × 12) ÷ медиана продажна цена €/м² × 100.</dd>
      <dt>Дял намалени обяви и среден дисконт</dt>
      <dd>Процент от активните обяви, чиято текуща цена е под първоначалната; средният дисконт е средната дълбочина на намалението при тях.</dd>
      <dt>Месеци до изчерпване (absorption)</dt>
      <dd>Активни обяви ÷ среден месечен брой свалени обяви за последните 6 месеца. По-висока стойност = по-бавен пазар.</dd>
      <dt>Достъпност</dt>
      <dd>Медианна цена на имот ÷ медианен годишен доход за региона (НСИ-стил). Резултатът е в „години доход“.</dd>
      <dt>Дни на пазара (DOM)</dt>
      <dd>Медиана на дните между първо и последно виждане за свалените обяви от последните 12 месеца.</dd>
    </dl>
  </section>

  <section class="card">
    <h2>Ниво 3 — аналитични индекси</h2>
    <dl>
      <dt>Хедоничен модел („Δ модел“ в Обяви)</dt>
      <dd>
        За всеки град се оценява регресия ln(€/м²) ~ квартал + тип имот + строителство +
        година на строеж + етаж (ridge OLS върху активните обяви). Отклонението на реалната
        цена от прогнозата показва дали обявата е над- или подценена спрямо пазара.
        <br />
        Реалните обяви често не посочват строителство, година или етаж. Такива обяви не се
        изхвърлят: липсващата категория влиза като собствено ниво „неизвестно“, а липсващото
        число се замества с медианата на наблюдаваните. Така извадката остава пълна, но
        прогнозата за обява без тези данни е по-груба.
      </dd>
      <dt>Индекс на прегряване (0–100)</dt>
      <dd>
        50 + 3,5 × (ръст на цените ГоГ − ръст на доходите ГоГ) + 2,5 × (ръст на цените ГоГ −
        ръст на наемите ГоГ), ограничено в [0; 100]. Около 50 = балансиран пазар; над 65 —
        цените изпреварват фундаментите; под 40 — пазарът изостава.
        <br />
        <b>Показва се „—“, когато няма година история за сравнение.</b> Не 50: 50 е отчитане на
        балансиран пазар, а не липса на данни, и двете не бива да изглеждат еднакво.
      </dd>
      <dt>Инерция на цените (моментум)</dt>
      <dd>Наклон (OLS) на месечния темп на растеж за последните 6 месеца — втора производна на цената. Положителна стойност = ускоряване.</dd>
      <dt>Градиент от центъра</dt>
      <dd>Наклон на ln(€/м²) спрямо километрите от центъра по кварталните медиани × 100 ≈ % промяна на цената на километър.</dd>
    </dl>
  </section>

  <section class="card">
    <h2>Макро индикатори</h2>
    <dl>
      <dt>PIR (Price-to-Income Ratio)</dt>
      <dd>Медианна цена на имот ÷ медианен годишен нетен доход за града (регионален доход по НСИ-стил × градски коефициент). Следи се и годишната промяна на самото съотношение, не само на цената.</dd>
      <dt>Изплащане чрез наем (P/R)</dt>
      <dd>Цена ÷ годишен брутен наем. Обратното на доходността, но в „години до изплащане“ — така аналитиците четат метриката по-често.</dd>
      <dt>Сделки / тримесечие</dt>
      <dd>
        Официален брой вписани сделки с недвижими имоти от Агенция по вписванията (Имотен регистър).
        Данните се публикуват на тримесечна база по служби по вписванията за наблюдаваните градове и
        общо за страната. Разминаване между цена и обем — растящи цени при спадащ брой сделки — е класически водещ индикатор за охлаждане на пазара.
      </dd>
      <dt>Лихва по ипотеки</dt>
      <dd>
        Лихва по нови жилищни кредити, ЕЦБ, месечно. Индикаторът „лихва ⇄ обем“ е корелация на
        месечните изменения на лихвата и на сделките за последните 12 месеца — насочващ, не
        строго статистически, и се появява само когато има поне 12 месеца от двата реда.
      </dd>
    </dl>
    <p class="removed">
      <b>Какво съзнателно липсва.</b> „Дял финансирани с ипотека“, „Инвестиционни купувачи“ и
      „Ново строителство“ бяха махнати. Това са показатели <b>по град</b>, а никой източник не ги
      публикува по град — съществуваха само защото можеха да бъдат измислени. Правдоподобно
      измислено число сред реални е по-подвеждащо от липсваща карта.
    </p>
  </section>

  <section class="card">
    <h2>Ограничения</h2>
    <dl>
      <dt>Офертни цени, не сделки</dt>
      <dd>
        Всичко, което идва от портала, е <b>искана</b> цена. Реално договорените са по-ниски.
        За сравнение: НСИ отчита за София около 1980 €/м² по сделки, докато медианата на
        обявите тук е над 2500 €/м². Разликата е очаквана и системна — не я четете като
        несъответствие, а като разлика между това, което се иска, и това, което се плаща.
      </dd>
      <dt>Историята на ниво обява започва от първото обхождане</dt>
      <dd>
        Порталите показват само днешното състояние — историята им не може да се свали. Затова
        МоМ, ГоГ, инерцията и прегряването на ниво обява стават възможни едва след като се
        натрупат месеци наши снимки. Дотогава те са „—“, а не 0. Тримесечният индекс на цените
        от Eurostat покрива историята от 2015 г., но той е на национално ниво.
      </dd>
      <dt>Дни на пазара са долна граница</dt>
      <dd>
        Само около една на шест обяви посочва дата на публикуване; останалите показват дата на
        последна корекция. За тях „първо виждане“ е денят, в който сме ги видели ние, така че
        реалното време на пазара е по-дълго от показаното.
      </dd>
      <dt>Официални вписани сделки, офертни цени</dt>
      <dd>
        Данните за броя на сделките идват директно от официалния Имотен регистър на Агенция по вписванията (реално изповядани нотариални актове).
        Цените обаче са офертни пазарни цени от обявите в порталите, тъй като вписаните актове в България не съдържат публично достъпни детайли за конкретните жилища и техните характеристики.
      </dd>
      <dt>Тънки пазари</dt>
      <dd>
        В най-малките градове предлагането е десетки обяви, не хиляди. Медиана върху такава
        извадка се движи силно от месец на месец; там числата са ориентир, не измерване.
      </dd>
    </dl>
    <p>
      Имотомер е аналитичен инструмент, не инвестиционен съвет.
    </p>
  </section>
</div>
